import { useState, useEffect } from 'react';
import './DynamicSettings.css';

export default function DynamicSettingsForm({
    panelType,
    config = {},
    setConfig,
    settingsSchema,
    materialBrowserProps,
}) {
  const [addingSetting, setAddingSetting] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);

    const isHidden = (def, panelType) => {
        const hiddenByPanelType =
            def.hiddenIfTypeIs &&
            (Array.isArray(def.hiddenIfTypeIs)
            ? def.hiddenIfTypeIs.includes(panelType)
            : def.hiddenIfTypeIs === panelType);

        const hiddenByDefType =
            def.hiddenIfIAm &&
            config.type !== undefined &&
            (Array.isArray(def.hiddenIfIAm)
            ? def.hiddenIfIAm.includes(config.type)
            : def.hiddenIfIAm === config.type);

        return hiddenByPanelType || hiddenByDefType;
    };

  // Ensure required fields like 'type' are initialized
    useEffect(() => {
        const requiredDefaults = {};
        Object.entries(settingsSchema).forEach(([key, def]) => {
            if (def.required && !config.hasOwnProperty(key) && !isHidden(def, panelType)) {
                if (def.type === 'boolean') requiredDefaults[key] = false;
                else if (def.type === 'select') requiredDefaults[key] = def.options[0];
                else if (def.type === 'list') requiredDefaults[key] = [];
                else requiredDefaults[key] = '';
            }
        });

        if (Object.keys(requiredDefaults).length > 0) {
            const newConfig = { ...config, ...requiredDefaults };
            if (JSON.stringify(newConfig) !== JSON.stringify(config)) {
              setConfig(newConfig);
            }
        }
    }, [config, setConfig, settingsSchema, panelType]);

  // Remove hidden keys from config when type changes
    useEffect(() => {
        if (!panelType) return;

        const newConfig = JSON.parse(JSON.stringify(config)); // deep clone
        let changed = false;

        Object.entries(settingsSchema).forEach(([key, def]) => {
            if (!settingsSchema.hasOwnProperty(key)) {
                delete newConfig[key];
                changed = true;
            }
            if (isHidden(def, panelType) && key in newConfig) {
                delete newConfig[key];
                changed = true;
            }
        });

        if (changed && JSON.stringify(newConfig) !== JSON.stringify(config)) {
          setConfig(newConfig);
        }
    }, [panelType, config, settingsSchema, setConfig]);

  // Visible required settings
  const requiredSettings = Object.entries(settingsSchema)
    .filter(([_, def]) => def.required)
    .filter(([key, def]) => !isHidden(def, panelType, config))

  // Visible optional settings
  const optionalSettings = Object.entries(settingsSchema)
    .filter(([_, def]) => !def.required)
    .filter(([key]) => config.hasOwnProperty(key))
    .filter(([key, def]) => !isHidden(def, panelType, config))

  // Addable (optional and not yet added) settings
  const addableSettings = Object.entries(settingsSchema)
    .filter(([_, def]) => !def.required)
    .filter(([key]) => !config.hasOwnProperty(key))
    .filter(([key, def]) => !isHidden(def, panelType, config))

  const handleChange = (key, value) => {
            setConfig({ 
            ...config,
            [key]: value
        });
    };

  const handleRemove = (key) => {
    const def = settingsSchema[key];
    if (def?.required) return;
    const { [key]: _, ...rest } = config;
    setConfig(rest);
  };

  const handleAddConfirm = () => {
    if (!addingSetting) return;
    const def = settingsSchema[addingSetting];
    if (!def) return;

    let defaultValue = '';
    if (def.type === 'boolean') defaultValue = false;
    else if (def.type === 'select') defaultValue = def.options[0];
    else if (def.type === 'list') defaultValue = [];
    else defaultValue = '';

    const newConfig = { ...config, [addingSetting]: defaultValue };
    setConfig(newConfig);
    setAddingSetting(null);
    setShowAddDropdown(false);
  };

  const renderInput = (key) => {
    const def = settingsSchema[key];
    const value = config[key];
    if (!def) return null;

    switch (def.type) {
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={!!value}
            onChange={e => handleChange(key, e.target.checked)}
          />
        );
      case 'select':
        return (
          <select value={value} onChange={e => handleChange(key, e.target.value)}>
            {def.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'list':
        return (
          <textarea
            value={Array.isArray(value) ? value.join('\n') : ''}
            onChange={e => handleChange(key, e.target.value.split('\n'))}
            rows={4}
            style={{ resize: 'vertical', width: '100%' }}
          />
        );
      case 'material':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={e => {
              handleChange(key, e.target.value);
              materialBrowserProps?.onMaterialType?.(e.target.value);
            }}
            onFocus={() => {
              // Register this field's setter and current value so the browser
              // pre-filters immediately without needing a keystroke
              materialBrowserProps?.onMaterialFocus?.(
                (newVal) => handleChange(key, newVal),
                value,
              );
            }}
            onBlur={() => materialBrowserProps?.onMaterialBlur?.()}
          />
        );
      case 'string':
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={e => handleChange(key, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="panel-settings-container">
      <ul className="settings-list">
        {[...requiredSettings, ...optionalSettings].length === 0 && (
          <li>No settings available.</li>
        )}
        {[...requiredSettings, ...optionalSettings].map(([key, def]) => (
          <li
            key={key}
            className={def.type === 'list' ? 'list-item' : ''}
          >
            <label>
              <strong>{def.label || key}:</strong>
              {renderInput(key)}
            </label>
            {!def.required && (
              <button
                onClick={() => handleRemove(key)}
                aria-label={`Remove ${key} setting`}
                className="remove-button"
              >
                −
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="add-setting-container">
        {!showAddDropdown && addableSettings.length > 0 && (
          <button
            onClick={() => setShowAddDropdown(true)}
            aria-label="Add new setting"
            className="add-button"
            type="button"
          >
            +
          </button>
        )}

        {showAddDropdown && (
          <>
            <select
              value={addingSetting || ''}
              onChange={e => setAddingSetting(e.target.value)}
              aria-label="Select setting to add"
            >
              <option value="" disabled>
                Select setting...
              </option>
              {addableSettings.map(([key, def]) => (
                <option key={key} value={key}>
                  {def.label || key}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddConfirm}
              disabled={!addingSetting}
              aria-label="Confirm add setting"
              className="add-button add-confirm"
              type="button"
            >
              +
            </button>
            <button
              onClick={() => {
                setShowAddDropdown(false);
                setAddingSetting(null);
              }}
              aria-label="Cancel add setting"
              className="cancel-button"
              type="button"
            >
              ×
            </button>
          </>
        )}
      </div>
    </div>
  );
}